/**
 * PersistentCrm - Keeps CRM iframe mounted across navigation
 *
 * Follows PersistentChatwoot pattern:
 * - Lazy mount: only renders after user first visits /crm
 * - visibility:hidden when navigating away (keeps connections alive)
 * - Shown instantly when returning to /crm
 *
 * URL sync: two-way browser URL ↔ iframe path synchronization
 * - Iframe ROUTE_CHANGE → updates browser URL (push for significant, replace for minor)
 * - Browser back/forward → sends NAVIGATE to iframe
 * - Loop prevention via parentInitiated flag + lastSyncedPath comparison
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { CrmEmbed } from "@/components/CrmEmbed";
import type { CrmEmbedRef } from "@/components/CrmEmbed/types";
import { useAuthStore } from "@/lib/store/authStore";
import { isSignificantNavigation } from "./urlSyncUtils";

export function PersistentCrm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnCrmPage = location.pathname.startsWith("/crm");

  // Lazy mount: only render after first visit
  const [hasVisitedCrm, setHasVisitedCrm] = useState(false);

  // URL sync refs — loop prevention + deep link initialPath
  const lastSyncedPath = useRef('');
  const initialPathRef = useRef<string | undefined>(undefined);
  const parentInitiated = useRef(false);
  const crmRef = useRef<CrmEmbedRef>(null);

  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  useEffect(() => {
    if (isOnCrmPage && !hasVisitedCrm) {
      // Extract sub-path: "/crm/leads/X" → "/leads/X", "/crm" → ""
      const subPath = location.pathname.slice(4); // Remove "/crm"
      initialPathRef.current = subPath || undefined;
      lastSyncedPath.current = subPath;
      setHasVisitedCrm(true);
    }
  }, [isOnCrmPage, hasVisitedCrm, location.pathname]);

  // Restore last-viewed URL when returning to CRM from another app (sidebar click)
  useEffect(() => {
    if (parentInitiated.current) return; // Back-button effect handles this
    if (isOnCrmPage && hasVisitedCrm && lastSyncedPath.current) {
      const expectedPath = `/crm${lastSyncedPath.current}`;
      if (location.pathname !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [isOnCrmPage, hasVisitedCrm, navigate, location.pathname]);

  // Back/forward button: when location changes while on CRM page,
  // send NAVIGATE to iframe if path differs from lastSyncedPath
  useEffect(() => {
    if (!isOnCrmPage || !hasVisitedCrm) return;
    const subPath = location.pathname.slice(4); // Remove "/crm"
    if (subPath && subPath !== lastSyncedPath.current) {
      if (!crmRef.current?.isReady) return;
      parentInitiated.current = true;
      lastSyncedPath.current = subPath;
      crmRef.current?.navigate(subPath);
      // Safety: reset parentInitiated if no ROUTE_CHANGE echo within 500ms
      const safetyTimer = setTimeout(() => {
        parentInitiated.current = false;
      }, 500);
      return () => clearTimeout(safetyTimer);
    }
  }, [location.pathname, isOnCrmPage, hasVisitedCrm]);

  const handleReady = useCallback(() => {
    console.log("[PersistentCrm] Ready");
  }, []);

  const handleRouteChange = useCallback((path: string, _title: string) => {
    // CRM sends paths relative to /crm base (Vue Router fullPath with base '/crm')
    if (parentInitiated.current) {
      parentInitiated.current = false;
      return; // Skip — we initiated this navigation via back/forward
    }
    if (lastSyncedPath.current === path) return; // Skip duplicate
    const prevPath = lastSyncedPath.current;
    lastSyncedPath.current = path;
    if (isOnCrmPage) {
      const shouldPush = isSignificantNavigation(prevPath, path);
      navigate(`/crm${path}`, { replace: !shouldPush });
    }
  }, [navigate, isOnCrmPage]);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentCrm] Error:", message);
  }, []);

  const handleNavigateParent = useCallback(
    (action: string) => {
      switch (action) {
        case "back":
          navigate(-1);
          break;
        case "home":
          navigate("/");
          break;
        default:
          if (action.startsWith("/")) {
            navigate(action);
          } else {
            console.warn("[PersistentCrm] Unknown navigate action:", action);
          }
      }
    },
    [navigate]
  );

  // Don't render if never visited or not provisioned
  if (!hasVisitedCrm || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnCrmPage ? "visible" : "hidden",
        pointerEvents: isOnCrmPage ? "auto" : "none",
      }}
    >
      <CrmEmbed
        ref={crmRef}
        initialPath={initialPathRef.current}
        className="h-full w-full"
        onReady={handleReady}
        onRouteChange={handleRouteChange}
        onError={handleError}
        onNavigateParent={handleNavigateParent}
      />
    </div>
  );
}
